import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Text,
  Tooltip
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { useAllImportRows } from '../../../services/import-service';

import { buildPreviewTree, humanize } from './util';

const matchLabel = record => {
  if (record.insuredMatch === 'new') {
    return 'new insured';
  }

  return record.insuredId ? `insured #${record.insuredId}` : 'insured';
};

const RecordCard = ({ record }) => (
  <Box border="1px solid" borderColor="greyScale.200" borderRadius="md" data-testid={`preview-record-${record.key}`} mb={2} p={3}>
    <Flex alignItems="center" flexWrap="wrap" gap={2} mb={2}>
      <Text textStyle="bodyMd500">{record.insuredName}</Text>
      <Badge colorScheme={record.insuredMatch === 'new' ? 'green' : 'blue'} variant="subtle">{matchLabel(record)}</Badge>
      {record.status === 'archived' && <Badge colorScheme="gray" variant="subtle">archived record</Badge>}
      {record.templateSource && record.templateSource !== 'none' && <Badge colorScheme="purple" variant="subtle">{`template: ${humanize(record.templateSource)}`}</Badge>}
      {record.templateSource === 'none' && record.status !== 'archived' && <Badge colorScheme="orange" variant="subtle">no template</Badge>}
    </Flex>
    {record.documents.map(document => (
      <Flex alignItems="center" gap={2} key={document.id} pl={2}>
        <Badge minW="78px" textAlign="center" variant="outline">{document.fileType ?? 'unknown'}</Badge>
        <Text textStyle="bodySm" wordBreak="break-all">{document.path}</Text>
        {document.fileStatus === 'archived' && <Text color="greyScale.500" textStyle="bodySm">archived</Text>}
        {document.rowStatus === 'warning' && (
          <Tooltip hasArrow label={document.issues.map(issue => <Text key={`${issue.code}-${issue.message}`}>{issue.message}</Text>)} placement="top">
            <Text color="orange.600" cursor="help" data-testid={`preview-warnings-${document.id}`} textDecoration="underline dotted" textStyle="bodySm">
              {`${document.issues.length || ''} warning${document.issues.length === 1 ? '' : 's'}`.trim()}
            </Text>
          </Tooltip>
        )}
      </Flex>
    ))}
  </Box>
);

RecordCard.propTypes = { record: PropTypes.object.isRequired };

/**
 * What the run will create, before it does: projects under their department,
 * the records inside each named after their insured, and the documents on each.
 */
const ImportPreview = ({ importData }) => {
  const version = `${importData.validatedAt ?? ''}|${importData.rowCounts?.total ?? 0}`;
  const { data: rows, isLoading, isError, error } = useAllImportRows(importData.id, version);

  if (isLoading) {
    return <Text>Loading the rows…</Text>;
  }

  if (isError) {
    return (
      <Alert borderRadius="md" status="error">
        <AlertIcon />
        <AlertDescription>{error?.message || 'The rows could not be loaded.'}</AlertDescription>
      </Alert>
    );
  }

  const tree = buildPreviewTree(rows, importData.name);
  const skipped = tree.skipped > 0 ? ` · ${tree.skipped} row(s) with errors will be skipped` : '';

  return (
    <Box>
      <Text color="greyScale.500" data-testid="preview-totals" mb={3} textStyle="bodySm">
        {`${tree.projects.length} project(s) · ${tree.recordCount} record(s) · ${tree.documentCount} document(s)${skipped}`}
      </Text>

      {tree.projects.length === 0 && <Text>Nothing would be created; fix the rows and validate again.</Text>}

      <Accordion allowMultiple defaultIndex={tree.projects.map((_, index) => index)}>
        {tree.projects.map(project => (
          <AccordionItem data-testid={`preview-project-${project.key}`} key={project.key}>
            <AccordionButton>
              <Flex alignItems="center" flex="1" gap={2} textAlign="left">
                <Text textStyle="bodyLg500">{project.name}</Text>
                <Badge colorScheme={project.existing ? 'blue' : 'green'} variant="subtle">{project.existing ? 'existing project' : 'new project'}</Badge>
                <Text color="greyScale.500" textStyle="bodySm">{`${project.department} · ${project.records.length} record(s)`}</Text>
              </Flex>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={3}>
              {project.records.map(record => <RecordCard key={record.key} record={record} />)}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

ImportPreview.propTypes = { importData: PropTypes.object.isRequired };

export default ImportPreview;
